// ============================================
// COMPONENT - Buscar Imagen Modal
// ============================================

import { useState } from 'react';
import { MdClose, MdSearch } from 'react-icons/md';
import { useBuscarImagenes } from '@almacenes/core';

interface BuscarImagenModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectImage: (imageUrl: string, imagenProductoId: number) => void;
  initialSearch?: string;
  productName?: string;
}

export const BuscarImagenModal = ({
  isOpen,
  onClose,
  onSelectImage,
  initialSearch = '',
  productName,
}: BuscarImagenModalProps) => {
  const [busquedaImagen, setBusquedaImagen] = useState(initialSearch);

  // Hook para buscar imágenes
  const { data: imagenesEncontradas, isLoading: buscandoImagenes } = useBuscarImagenes(busquedaImagen);

  const handleSeleccionarImagen = (url: string, id: number) => {
    onSelectImage(url, id);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-start justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl my-8 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header del modal */}
        <div className="flex items-center justify-between p-6 border-b-2 border-gray-200">
          <h2 className="text-2xl font-bold" style={{ color: '#11194F' }}>
            {productName ? `Buscar imagen: ${productName}` : 'Buscar imagen del producto'}
          </h2>
          <button
            onClick={() => {
              onClose();
              setBusquedaImagen('');
            }}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            style={{ color: '#11194F' }}
          >
            <MdClose className="text-3xl" />
          </button>
        </div>

        {/* Buscador */}
        <div className="p-6 border-b-2 border-gray-200">
          <div className="relative">
            <MdSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-gray-400" />
            <input
              type="text"
              value={busquedaImagen}
              onChange={(e) => setBusquedaImagen(e.target.value)}
              placeholder="Escribe al menos 3 caracteres para buscar..."
              className="w-full pl-12 pr-4 py-4 text-lg border-2 rounded-xl focus:outline-none focus:border-primary transition-colors"
              style={{ borderColor: '#E5E7EB' }}
              autoFocus
            />
          </div>
          {busquedaImagen.length > 0 && busquedaImagen.length < 3 && (
            <p className="mt-2 text-sm text-gray-500">
              Escribe al menos {3 - busquedaImagen.length} caracteres más para buscar
            </p>
          )}
        </div>

        {/* Resultados */}
        <div className="flex-1 overflow-y-auto p-6">
          {buscandoImagenes && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
              <p className="text-gray-600">Buscando imágenes...</p>
            </div>
          )}

          {!buscandoImagenes && busquedaImagen.length >= 3 && (!imagenesEncontradas || imagenesEncontradas.length === 0) && (
            <div className="flex flex-col items-center justify-center py-12">
              <p className="text-xl text-gray-600">No se encontraron imágenes</p>
              <p className="text-gray-500 mt-2">Intenta con otra búsqueda</p>
            </div>
          )}

          {!buscandoImagenes && imagenesEncontradas && imagenesEncontradas.length > 0 && (
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Se encontraron {imagenesEncontradas.length} imágenes
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {imagenesEncontradas.map((imagen) => (
                  <button
                    key={imagen.id}
                    onClick={() => handleSeleccionarImagen(imagen.url, imagen.id)}
                    className="group relative border-2 border-gray-200 rounded-xl p-3 hover:border-primary hover:shadow-lg transition-all"
                  >
                    <div className="aspect-square flex items-center justify-center bg-gray-50 rounded-lg overflow-hidden mb-2">
                      <img
                        src={imagen.url}
                        alt={imagen.descripcion}
                        className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform"
                      />
                    </div>
                    <p className="text-xs text-gray-700 line-clamp-2 mb-1">
                      {imagen.descripcion}
                    </p>
                    <p className="text-xs text-gray-500">
                      {imagen.fuente}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {busquedaImagen.length < 3 && !buscandoImagenes && (
            <div className="flex flex-col items-center justify-center py-12">
              <MdSearch className="text-6xl text-gray-300 mb-4" />
              <p className="text-xl text-gray-600">Busca una imagen</p>
              <p className="text-gray-500 mt-2">Escribe el nombre del producto para ver resultados</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
